import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import CircleActiveFilterBtn from '../../../particles/CircleActiveFilterBtn'
import CloseIcon from '../../../icons/IconLib/XIcon'
import AcceptIcon from '../../../icons/IconLib/CheckIcon'
import { DrawerState, PalletDetailFormData } from '../hooks/useEntryPallets'
import { IPalletDetails } from '../../../../modules/API/Pallets/models/palletDetails'
import PalletDetailForm from './PalletDetailForm'

type Props = {
  drawerState: DrawerState
  onClose: () => void
  onCreate: (data: PalletDetailFormData) => void
  onUpdate: (detail: IPalletDetails, data: PalletDetailFormData) => void
}

const buildInitialFormData = (detail: IPalletDetails | null): PalletDetailFormData => ({
  customerID: detail?.CustomerID ?? 0,
  salePrice: detail?.SalePrice ?? 0,
  weightNeto: detail?.WeightNeto ?? 0,
  quantity: detail?.Quantity ?? 0,
  remarks: detail?.Remarks ?? '',
})

const PalletDetailDrawer = ({ drawerState, onClose, onCreate, onUpdate }: Props) => {
  const { isOpen, mode, detail } = drawerState

  const [formData, setFormData] = useState<PalletDetailFormData>(() =>
    buildInitialFormData(detail)
  )

  // Reset form every time the drawer opens or the detail changes.
  // Running on close too ensures stale values never linger between sessions.
  useEffect(() => {
    setFormData(buildInitialFormData(detail))
  }, [isOpen, detail])

  const handleFormChange = (partial: Partial<PalletDetailFormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }))
  }

  const handleSave = () => {
    if (mode === 'edit' && detail) {
      onUpdate(detail, formData)
    } else {
      onCreate(formData)
    }
  }

  return (
    <>
      <Backdrop $isOpen={isOpen} onClick={onClose} aria-hidden="true" />
      <Sheet $isOpen={isOpen} role="dialog" aria-modal="true" aria-label="עריכת פרטי משטח">
        <SheetHeader>
          <CircleActiveFilterBtn func={onClose} aria-label="סגור">
            <span style={{ display: 'inline-flex', cursor: 'pointer' }} onClick={onClose}><CloseIcon /></span>
          </CircleActiveFilterBtn>
          <CircleActiveFilterBtn func={handleSave} aria-label="שמור">
            <AcceptIcon />
          </CircleActiveFilterBtn>
        </SheetHeader>

        <SheetBody>
          <PalletDetailForm formData={formData} onChange={handleFormChange} />
        </SheetBody>
      </Sheet>
    </>
  )
}

const Backdrop = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 20;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: opacity 0.35s ease;
`

const Sheet = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 66%;
  background: #fff;
  border-radius: 30px 30px 0 0;
  z-index: 21;
  display: flex;
  flex-direction: column;
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(105%)')};
  transition: transform 0.38s cubic-bezier(0.32, 0.72, 0, 1);
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.15);
`

const SheetHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0.5em 0.5em 0.5em;
  flex-shrink: 0;
`

const SheetBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5em 1.2em 2em;
 

  &::-webkit-scrollbar {
    width: 0;
  }
`

export default PalletDetailDrawer
